import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import UserService from '_services/UserService';
import { UserPayload } from '_types/User';

interface CurrentUserState {
  currentUser: any;
  accountDetails: any;
  isAuthenticated: boolean;
  isLoading: boolean;
  redirectAfterLogin: string | null;
}

const initialState: CurrentUserState = {
  currentUser: null,
  accountDetails: null,
  isAuthenticated: false,
  isLoading: true,
  redirectAfterLogin: null,
};

// Async thunk for syncing user account
export const syncUserAccount = createAsyncThunk(
  'auth/syncUserAccount',
  async () => {
    const response = await UserService.syncUserAccount();
    return response;
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCurrentUser: (state, action: PayloadAction<any>) => {
      state.currentUser = action.payload;
      state.isAuthenticated = true;
      state.isLoading = false;
    },
    setAccountDetails: (state, action: PayloadAction<any>) => {
      state.accountDetails = action.payload;
      localStorage.setItem(
        'accountDetails',
        JSON.stringify(state.accountDetails),
      );
    },
    clearCurrentUser: state => {
      state.currentUser = null;
      state.accountDetails = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      // Clear localStorage when clearing user
      if (typeof window !== 'undefined') {
        localStorage.removeItem('currentUser');
        localStorage.removeItem('accountDetails');
        localStorage.removeItem('afristore_auth_state');
      }
    },
    updateUserProfile: (state, action: PayloadAction<Partial<UserPayload>>) => {
      if (state.currentUser) {
        state.currentUser = { ...state.currentUser, ...action.payload };
        // Update localStorage with new user data
        localStorage.setItem('currentUser', JSON.stringify(state.currentUser));
        localStorage.setItem(
          'accountDetails',
          JSON.stringify(state.accountDetails),
        );
      }
    },
    setRedirectAfterLogin: (state, action: PayloadAction<string | null>) => {
      state.redirectAfterLogin = action.payload;
    },
    clearRedirectAfterLogin: state => {
      state.redirectAfterLogin = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(syncUserAccount.pending, state => {
        state.isLoading = true;
      })
      .addCase(syncUserAccount.fulfilled, (state, action) => {
        state.accountDetails = action.payload;
        state.isLoading = false;
        // Update localStorage with account details
        localStorage.setItem('accountDetails', JSON.stringify(action.payload));
      })
      .addCase(syncUserAccount.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const {
  setCurrentUser,
  setAccountDetails,
  clearCurrentUser,
  updateUserProfile,
  setRedirectAfterLogin,
  clearRedirectAfterLogin,
} = authSlice.actions;

export default authSlice.reducer;
